import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { SidebarRail, type RailNavItem } from "@/components/layout/sidebar-rail";
import { SettingsSidebar } from "@/pages/settings/settings-sidebar";
import { useAuth } from "@/hooks/use-auth";

type Props = {
  open: boolean;
  onClose: () => void;
  brandTo: string;
  primaryNav: RailNavItem[];
  userMenu: React.ReactNode;
};

/**
 * Mobile-only slide-in drawer triggered by the TopBar hamburger.
 *
 * Contextual behavior — matches Frame.io's mobile pattern:
 *   - On regular routes: renders the app icon rail (Home / Search /
 *     Notifications / Help / Account) only.
 *   - On settings routes (/app/settings/*, /admin/settings/*): ALSO renders
 *     the SettingsSidebar to the right of the rail, so the user can jump
 *     between settings sub-pages without a second hamburger.
 *
 * Auto-closes when the route changes so tapping a link in either rail takes
 * the user straight to the page.
 */
export function MobileNavDrawer({ open, onClose, brandTo, primaryNav, userMenu }: Props) {
  const { pathname } = useLocation();
  const { role } = useAuth();

  // Route detection — settings drawer variant. basePath drives the NavLinks
  // inside SettingsSidebar so we route to the right tree (app vs admin).
  let settingsBasePath: "/app/settings" | "/admin/settings" | null = null;
  if (pathname.startsWith("/app/settings")) settingsBasePath = "/app/settings";
  else if (pathname.startsWith("/admin/settings")) settingsBasePath = "/admin/settings";

  // Close whenever route changes — drawer opened to let the user pick a page;
  // once they've picked one, get out of their way.
  useEffect(() => {
    if (open) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true" aria-label="Navigation">
      {/* Backdrop — tap to close */}
      <button
        type="button"
        aria-label="Close navigation"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 backdrop-blur-[1px] animate-in fade-in duration-150"
      />
      {/* Panel(s) — icon rail always, plus optional settings sidebar */}
      <div
        className="relative flex h-full animate-in slide-in-from-left duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <SidebarRail brandTo={brandTo} primaryNav={primaryNav} userMenu={userMenu} />

        {/* Settings sub-nav — only rendered on settings routes. SettingsSidebar
            already brings its own w-64 + h-full + own scroll, so it slots in
            next to the rail cleanly. */}
        {settingsBasePath && (
          <SettingsSidebar basePath={settingsBasePath} role={role} />
        )}

        {/* Close (X) — floats just outside the drawer's right edge */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close navigation"
          className="absolute top-4 grid h-9 w-9 place-items-center rounded-full bg-secondary text-muted-foreground shadow-sm transition-colors hover:text-foreground"
          style={{ left: settingsBasePath ? "calc(76px + 16rem + 12px)" : "84px" }}
        >
          <X className="h-4 w-4" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
